import { Card } from "@/components/ui/card";
import { Book, Upload, Share, Archive, Scan, FileText, Globe, Database, LucideIcon } from "lucide-react";
import { useWebsiteContent } from "@/contexts/WebsiteContentContext";
import { usePreview } from "@/contexts/PreviewContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { useEffect, useRef, useState } from "react";

// Icon mapping helper
const iconMap: Record<string, LucideIcon> = {
  book: Scan,           // Book Digitization - scanning physical books
  upload: FileText,     // Digital Publishing - document formatting
  share: Globe,         // Distribution - global reach
  archive: Database,    // Content Preservation - data storage
};

const Services = () => {
  const { content, isLoading } = useWebsiteContent();
  const { previewContent, isPreviewMode } = usePreview();
  const { language } = useLanguage();
  const lang = (language.startsWith('en') ? 'en' : 'ar') as 'en' | 'ar';

  // Use preview content when in preview mode, otherwise use regular content
  const activeContent = (isPreviewMode && previewContent) ? previewContent : content;
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  // All hooks must be called before any early returns
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Add delay like statistics cards
            setTimeout(() => setIsVisible(true), 200);
          }
        });
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    // Also show after content loads (fallback for refresh)
    if (!isLoading && content?.services?.services?.length > 0) {
      setTimeout(() => setIsVisible(true), 500);
    }

    return () => observer.disconnect();
  }, [isLoading, content]);

  // Don't render if content is still loading
  if (isLoading) {
    return (
      <section id="services" className="py-24 bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary"></div>
      </section>
    );
  }

  const services = activeContent?.services;


  // Simplified safety check
  if (!services) {
    return (
      <section id="services" className="py-24 bg-background">
        <div className="container px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto text-center">
          <p className="text-muted-foreground">Services content not available</p>
        </div>
      </section>
    );
  }

  return (
    <section ref={sectionRef} id="services" className="py-24 bg-background">
      <div className="container px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto">
        <div className="text-center mb-20">
          <h2 className="text-4xl sm:text-5xl font-bold text-foreground mb-4">
            {services.title?.[lang] || 'Our Services'}
          </h2>
          <p className="text-lg text-foreground/70 max-w-2xl mx-auto">
            {services.subtitle?.[lang] || 'Professional publishing services'}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {(services.services || []).map((service, index) => {
            const IconComponent = iconMap[service.icon] || Book;
            return (
              <Card
                key={index}
                className={`p-8 text-center border border-primary/20 bg-white shadow-md hover:shadow-xl hover:scale-105 hover:-translate-y-2 hover:border-primary/40 cursor-pointer group rounded-2xl transition-all duration-1000 ease-out transform ${
                  isVisible
                    ? 'translate-y-0 opacity-100'
                    : 'translate-y-8 opacity-0'
                }`}
                style={{
                  transitionDelay: isVisible ? `${index * 500}ms` : '0ms'
                }}
              >
                <div className="flex justify-center mb-6">
                  <div className="p-4 rounded-full bg-primary/15 border border-primary/30 group-hover:bg-primary/25 group-hover:scale-110 transition-all duration-300">
                    <IconComponent className="h-8 w-8 text-primary group-hover:text-primary group-hover:scale-110 transition-transform duration-300" />
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-3 group-hover:text-primary transition-colors duration-300">
                  {service.title?.[lang] || 'Service Title'}
                </h3>
                <p className="text-sm text-foreground/70 leading-relaxed">
                  {service.description?.[lang] || 'Service description'}
                </p>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;
